import type { AppProps } from 'next/app';
import { I18nProvider } from 'next-localization';
import { ErrorBoundary } from 'react-error-boundary';
import Bootstrap from 'src/Bootstrap';
import { Render500Fallback } from 'src/Render500Fallback';
import { SearchProvider } from 'lib/utils/coveo/SearchContext/SearchContext';
import { useBrowserNavigation } from 'lib/hooks/useBrowserNavigation';
import { JSX } from 'react';

import 'assets/app.css';
import { AppointmentModalProvider } from 'helpers/Context/AppointmentModalProvider';
import ScrollToTopOnBack from 'components/ScrollToTopOnBack';
import useForceHardReload from 'lib/hooks/useForceHardReload';
import { SitecorePageProps } from '@sitecore-content-sdk/nextjs';

function App({ Component, pageProps, router }: AppProps<SitecorePageProps>): JSX.Element {
  const { dictionary, ...rest } = pageProps;

  // Handle browser back/forward navigation properly
  useBrowserNavigation();
  // Hack to force full reload when user navigates away from pages with DOM manipulation
  useForceHardReload();

  const locale = pageProps.page?.locale || 'en';

  return (
    <>
      <Bootstrap {...pageProps} />
      {/*
        // Use the next-localization (w/ rosetta) library to provide our translation dictionary to the app.
        // Note Next.js does not (currently) provide anything for translation, only i18n routing.
        // If your app is not multilingual, next-localization and references to it can be removed.
      */}
      <I18nProvider lngDict={dictionary} locale={locale}>
        <ErrorBoundary
          fallbackRender={({ resetErrorBoundary, error }) => (
            <Render500Fallback
              router={router}
              locale={locale}
              Component={Component}
              error={error}
              resetErrorBoundary={resetErrorBoundary}
            />
          )}
        >
          <ScrollToTopOnBack />
          <SearchProvider>
            <AppointmentModalProvider>
              <Component {...rest} />
            </AppointmentModalProvider>
          </SearchProvider>
        </ErrorBoundary>
      </I18nProvider>
    </>
  );
}

export default App;
